/**
 * Fondation feature flags gating the student portal. Toggled by an administrator at
 * `/core/features`; the declarations live in `backend/Parcours.Core/Features/`, and these keys
 * must match theirs exactly — a key the catalog does not know reads as `false`, which HIDES the
 * feature rather than exposing it.
 *
 * Read through `useSdk().Features.isEnabled(...)` from `@fondationcss/sdk`, which is reactive:
 * the value flips once the boot-time flag load resolves, so a view must react to it rather than
 * sample it once at setup.
 *
 * (The submissions flag is not here: it lives with the rest of its vertical, in
 * `student-submission-utils.ts`.)
 */

/**
 * « Répéter sur les jours suivants de la semaine » in the student dialog
 * (`StudentRepeatWeekFeature.KEY`).
 */
export const STUDENT_REPEAT_WEEK_FEATURE = 'StudentRepeatWeek';

/**
 * What the module plan shows the student — the « Prévu » ghost tiles AND the in-dialog
 * suggestion (`StudentPlannedSuggestionFeature.KEY`). One key for both: they disclose the same
 * fact, so gating them apart would hide the affordance while leaking the information.
 */
export const STUDENT_PLANNED_SUGGESTION_FEATURE = 'StudentPlannedSuggestion';

/**
 * The « Rapports » page — menu entry, view, and the two self-service report routes it calls
 * (`StudentReportsFeature.KEY`). On by default. The schedule PDF route is NOT behind it: the
 * schedule page itself offers that PDF (#959).
 */
export const STUDENT_REPORTS_FEATURE = 'StudentReports';

/**
 * Competency self-assignment by the student in an individualized enrolment
 * (`StudentSelfAssignmentFeature.KEY`): what the student may choose on their assignment board,
 * and the staff lock that restrains them — « Tout verrouiller » / « Tout déverrouiller » and the
 * per-competence padlock of the sequencer.
 *
 * ON by default: the mechanism already runs in every deployment, so the flag exists to let a
 * centre decline the practice, not to make it opt in. Read it through
 * {@link isEnabledUnlessTurnedOff} and NEVER through `Features.isEnabled` — the latter answers
 * false for a key it does not hold yet, which would withdraw the feature for the frame before the
 * flags land, and for good if that load ever fails.
 */
export const STUDENT_SELF_ASSIGNMENT_FEATURE = 'StudentSelfAssignment';

/**
 * Reads a flag whose backend declaration is `DefaultEnabled = true`.
 *
 * `Features.isEnabled` answers `false` for a key it does not hold yet — the right default for a
 * gate that HIDES something, the wrong one for a feature meant to be on: it would withdraw the
 * feature for the frame before the flags land, and for good if that load ever fails. Here only an
 * explicit `false` counts; anything else leaves the declared default in place.
 *
 * Pass `useSdk().Features.flags.value` so the reading stays reactive.
 */
export function isEnabledUnlessTurnedOff(flags: Record<string, boolean>, key: string): boolean {
  return !(key in flags) || flags[key];
}
